import {
  Button,
  Dialog,
  Flex,
  InputContainer,
  LoaderCover,
  RadioGroup,
  Text,
  TextField,
  UserPlusIcon,
  toaster,
} from 'components'
import { useWorkspace } from 'components/providers'
import { forwardRef, useState } from 'react'
import { WorkspaceUser } from 'types'
import { createTimestamp, forceLight, sendInvite, useTheme, validEmail } from 'utils'
import PermissionWithHelp, { permissionOptions } from './PermissionWithHelp'

interface Props {
  open: boolean
  onClose: () => void
  onComplete: (user?: WorkspaceUser) => void
  existingUsers?: string[]
}

const NewUserForm = forwardRef<HTMLInputElement, Props>(
  ({ open, onClose, onComplete: onDone, existingUsers }, ref) => {
    const { updateWorkspace, workspace } = useWorkspace()
    const { theme } = useTheme()

    const [data, setData] = useState<WorkspaceUser>({
      email: '',
      permission: 'editor',
      created: createTimestamp(),
    })
    const [creating, setCreating] = useState(false)

    const { id, pendingUserByEmail } = workspace || {}
    const cleanEmail = data.email.trim().toLowerCase()
    const existingUser = !creating && existingUsers?.includes(cleanEmail)

    async function handleCreate() {
      if (!data) return

      setCreating(true)

      try {
        await sendInvite({ email: cleanEmail, workspaceId: id! })

        const cleanData = { ...data, email: cleanEmail }
        const updatedPendingUserByEmail = { ...pendingUserByEmail, [cleanEmail]: cleanData }
        await updateWorkspace({ pendingUserByEmail: updatedPendingUserByEmail })

        onDone()
        setData({ email: '', permission: 'editor', created: createTimestamp() })
      } catch (e: any) {
        console.error(e)
        toaster.create({
          title: 'Failed to send invite',
          description: e.message,
          type: 'error',
        })
      } finally {
        setCreating(false)
      }
    }

    function updateData(body: Partial<WorkspaceUser>) {
      setData(s => ({ ...s, ...body }))
    }

    return (
      <Dialog
        contentProps={forceLight}
        onClose={onClose}
        closeOnInteractOutside={false}
        open={open}
        size={{ base: 'sm', sm: 'lg' }}
        title={
          <Flex align={'center'} gap={2}>
            <UserPlusIcon />
            <Text>Invite Member</Text>
          </Flex>
        }
        footer={
          <Flex justify="space-between" w="100%">
            <Button onClick={onClose} variant="outline">
              Cancel
            </Button>

            <Button
              alignSelf="flex-end"
              disabled={!validEmail(cleanEmail) || Boolean(existingUser)}
              onClick={handleCreate}
              palette={theme === 'light' ? 'primary' : 'secondary'}
            >
              Send Invite
            </Button>
          </Flex>
        }
      >
        <LoaderCover show={creating}>
          <Flex direction="column" gap={2} m={4}>
            <TextField
              label="Email Address:"
              ref={ref}
              bg={'#fff'}
              backdropFilter={'blur(27px)'}
              borderRadius={'10px'}
              type="email"
              placeholder="Email"
              value={data.email}
              isError={existingUser}
              helperText={existingUser ? 'User already exists' : undefined}
              onChange={e => updateData({ email: e.target.value })}
            />
            <InputContainer label="Permission:">
              <RadioGroup
                horizontal
                onChange={permission => updateData({ permission })}
                value={data.permission}
                options={permissionOptions.map(({ value }) => ({
                  label: <PermissionWithHelp value={value} />,
                  value,
                }))}
              ></RadioGroup>
            </InputContainer>
          </Flex>
        </LoaderCover>
      </Dialog>
    )
  }
)

export default NewUserForm
